import { Flex, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { TableColumn } from "@amzn/stencil-react-components/table";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import React from "react";
import capitalize from "lodash/capitalize";
import { Order } from "../../orders.types";
import { OrderMessagesIcon } from "@/common/components/OrderMessagesIcon/order-messages-icon";
import { UserPermissions } from "@/services/user-permissions/user-permissions";
import { EmptyState } from "@/common/components/EmptyState/empty-state";

const EMPTY_STATE_HEADING = "Nothing here yet";
const EMPTY_STATE_DESCRIPTION = "Your hardware orders will show up here once you place one.";
const ERROR_STATE_HEADING = "We couldn't load your orders";
const ERROR_STATE_DESCRIPTION = "Something went wrong loading your orders. Please try again.";
const ORDERS_STATE_ILLUSTRATION = "catPlaysFolder";

export const OrdersLoadingView: React.FC = () => (
  <Flex flexDirection="column">
    <View alignSelf="center">
      <Spacer height={32} width={32} />
      <Spinner size={SpinnerSize.Medium} showText loadingText="Loading your orders" />
    </View>
  </Flex>
);

export const OrdersErrorState: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <EmptyState
    illustration={ORDERS_STATE_ILLUSTRATION}
    isAlert
    heading={ERROR_STATE_HEADING}
    description={ERROR_STATE_DESCRIPTION}
    dataTestId="orders-load-error"
    action={
      onRetry && (
        <Button variant={ButtonVariant.Primary} onClick={onRetry} data-testid="orders-retry">
          Try again
        </Button>
      )
    }
  />
);

export const OrdersEmptyState: React.FC = () => (
  <EmptyState
    illustration={ORDERS_STATE_ILLUSTRATION}
    heading={EMPTY_STATE_HEADING}
    description={EMPTY_STATE_DESCRIPTION}
    dataTestId="no-orders"
  />
);

export const buildStatusLabel = (value: string): string =>
  value === "PENDING_APPROVAL" ? "Waiting for approval" : capitalize(value).replace("_", " ");

export const buildDateComponent = (value: string | undefined) =>
  value ? <span>{new Date(value).toLocaleDateString()}</span> : <span>N/A</span>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const messagesColumn: TableColumn<any> = {
  header: "Messages",
  accessor: ({ data }: { data: Order }) => {
    if (!data.orderSupport?.supportRequested) return null;
    return (
      <OrderMessagesIcon
        hasOrderSupport={data.orderSupport.supportRequested}
        lastCommentOn={data.orderSupport.lastCommentInfo?.on}
        lastCommentBy={data.orderSupport.lastCommentInfo?.by}
        orderId={data.orderId}
        currentUser={UserPermissions.current.username ?? ""}
        title={data.items?.[0]?.itemInformation?.name ?? ""}
        orderReference={data.orderReference ?? ""}
        orderTaxId={data.orderId}
      />
    );
  },
};
